export const distributorPageData = {
  stats: [
    { value: "50%", label: "Distribution Revenue Growth" },
    { value: "70%", label: "Faster Onboarding" },
    { value: "60%", label: "Fewer Order Errors" },
    { value: "100%", label: "Network Visibility" },
  ],
  statsAccent: "from-teal-600 to-cyan-600",

  challengeTitle: "Growing Your Distributor Network Without Losing Control",
  challengeSubtitle:
    "Scaling distribution manually creates chaos — missed orders, invisible performance issues, and eroded relationships.",
  challenges: [
    {
      icon: "📋",
      title: "Manual Distributor Onboarding",
      description:
        "Onboarding new distributors through paperwork and email takes weeks — slowing your network expansion.",
    },
    {
      icon: "📊",
      title: "No Performance Visibility",
      description:
        "Without data, you don't know which distributors are performing, struggling, or holding hidden inventory.",
    },
    {
      icon: "💬",
      title: "Fragmented Communication",
      description:
        "Orders, updates, and disputes handled via phone and email create confusion and missed commitments.",
    },
    {
      icon: "💰",
      title: "Credit & Payment Risk",
      description:
        "Extended credit to distributors without real-time exposure monitoring creates bad debt and cash flow risk.",
    },
    {
      icon: "🗺️",
      title: "Territory Overlap & Gaps",
      description:
        "Without territory mapping, distributor coverage is uneven — creating expensive conflicts and missed markets.",
    },
    {
      icon: "📦",
      title: "Inventory Visibility Gaps",
      description:
        "You can't see what stock distributors hold, leading to over-supply in some areas and stock-outs in others.",
    },
  ],
  theme: {
    challengeBg: "bg-slate-900",
    challengeTitle: "text-white",
    challengeSubtitle: "text-slate-400",
    challengeCard: "bg-slate-800/60",
    challengeBorder: "border-slate-700",
    badgeBg: "bg-teal-50 text-teal-700 border-teal-100",
    solutionIconBg: "bg-teal-50",
    solutionIconColor: "text-teal-600",
    solutionAccent: "from-teal-600 to-cyan-600",
  },

  solutionTitle: "Automate, Scale, and Optimise Your Entire Distributor Network",
  solutionSubtitle:
    "One intelligent platform to manage every distributor relationship from onboarding to performance optimisation.",
  solutions: [
    {
      icon: "⚡",
      title: "Digital Distributor Onboarding",
      description:
        "Onboard new distributors in minutes with digital qualification, contract management, and automated system access.",
    },
    {
      icon: "🏷️",
      title: "Tiered Pricing & Credit Management",
      description:
        "Set distributor-specific pricing tiers, credit limits, and payment terms — managed automatically per account.",
    },
    {
      icon: "📊",
      title: "Real-Time Performance Dashboards",
      description:
        "Monitor every distributor's order volume, payment performance, and territory coverage in a live dashboard.",
    },
    {
      icon: "🤖",
      title: "AI Demand Forecasting by Territory",
      description:
        "Predict demand by geographic region to allocate inventory optimally and prevent territory-level stockouts.",
    },
    {
      icon: "🔔",
      title: "Automated Order Management",
      description:
        "Distributors place and track orders digitally — with automated confirmation, status updates, and delivery tracking.",
    },
    {
      icon: "🌐",
      title: "Global Distributor Network Access",
      description:
        "Discover and connect with verified regional and international distributors to expand your market reach instantly.",
    },
    {
      icon: "📈",
      title: "Territory Optimisation Engine",
      description:
        "AI analyses coverage gaps, order frequency, and logistics costs to recommend optimal territory assignments.",
    },
    {
      icon: "🛡️",
      title: "Credit Risk Monitoring",
      description:
        "Real-time credit exposure alerts and automated payment enforcement protect you from distributor bad debt.",
    },
    {
      icon: "🔗",
      title: "ERP & System Integration",
      description:
        "Seamlessly connect distributor data to your ERP, accounting, and logistics systems for unified operations.",
    },
  ],

  howItWorksTitle: "Scale Your Distribution Network in 4 Steps",
  howItWorksSteps: [
    {
      icon: "📋",
      title: "Onboard Digitally",
      description: "Distributors complete digital onboarding — qualification, contract, and system access in minutes.",
    },
    {
      icon: "⚙️",
      title: "Configure Terms",
      description: "Set pricing tiers, credit limits, territory boundaries, and performance KPIs per distributor.",
    },
    {
      icon: "🚀",
      title: "Activate the Network",
      description: "Distributors start ordering digitally while you monitor performance in real time from day one.",
    },
    {
      icon: "📈",
      title: "Optimise Continuously",
      description: "AI identifies top performers, underperforming territories, and expansion opportunities continuously.",
    },
  ],

  outcomeTitle: "Distribution Network Results That Drive Business Growth",
  outcomeSubtitle: "Measurable outcomes from brands and manufacturers using EkkBaz AI.",
  outcomes: [
    {
      value: "50%",
      metric: "Distribution Revenue Growth",
      description:
        "Average revenue increase from distribution channels within 12 months — driven by network expansion and AI-optimised territory management.",
    },
    {
      value: "70%",
      metric: "Faster Distributor Onboarding",
      description:
        "Digital onboarding reduces the time to activate a new distributor from weeks to under 48 hours.",
    },
    {
      value: "60%",
      metric: "Fewer Order Errors",
      description:
        "Digital order management and automated confirmation eliminate manual errors that cause costly disputes.",
    },
  ],
  outcomeImage: "/assets/images/products/distributor2.png",

  ctaTitle: "Scale Your Distribution Network Without Adding Headcount",
  ctaSubtitle:
    "Book a consultation to discover how EkkBaz AI can expand your distributor network and grow distribution revenue by 50%.",
  ctaLabel: "Book a Free Consultation",
  ctaLink: "https://outlook.office.com/book/EkkBazInvestment@EkkBaz.com/?ismsaljsauthenabled",
};

export const distributorData = [
  {
    id: 1,
    extraLabel: "The Challenge",
    userType: "Scaling Distribution Without Losing Visibility or Control",
    description:
      "As distribution networks grow, managing hundreds of distributors manually becomes impossible. Paper-based processes, fragmented communication, and lack of real-time data lead to missed orders, inventory gaps, and eroded distributor relationships — limiting your growth ceiling.",
    banner: "/assets/images/products/distributor1.png",
  },
  {
    id: 2,
    extraLabel: "Our Solution",
    userType: "Automate, Scale, and Optimize Your Entire Distributor Network",
    description:
      "EkkBaz AI provides a complete distributor management platform — from automated product listings and role-based access management to real-time order tracking and performance analytics. Onboard new distributors digitally in minutes, set pricing tiers, manage credit limits, and monitor every order from placement to delivery with full transparency.",
    banner: "/assets/images/products/distributor2.png",
  },
  {
    id: 3,
    extraLabel: "The Outcome",
    userType: "Connect with a Global Distributor Network and Unlock AI-Driven Growth",
    description:
      "EkkBaz AI helps manufacturers and brands expand their distributor network with access to verified regional and global distributors. AI-powered analytics provide deep insights into distributor performance, demand forecasting by territory, and supply chain optimization — helping businesses scale distribution revenue by 50% without adding headcount.",
    banner: "/assets/images/products/distributor3.png",
  },
];

export const distributorFAQs = [
  {
    question: "How does EkkBaz AI help manage distributor relationships?",
    answer:
      "EkkBaz AI provides a centralized platform for all distributor communications, order management, and performance tracking. You can set pricing tiers, manage credit limits, and monitor distributor performance with real-time data — maintaining strong relationships at scale.",
  },
  {
    question: "Can EkkBaz AI help onboard new distributors faster?",
    answer:
      "Yes. EkkBaz AI enables digital distributor onboarding with automated qualification, contract management, and system access provisioning — reducing onboarding time from weeks to days.",
  },
  {
    question: "How does EkkBaz AI optimize distributor territory management?",
    answer:
      "EkkBaz AI analyzes demand patterns, order frequency, and coverage gaps by geographic territory, and recommends optimal distributor assignments and inventory allocation to maximize coverage and minimize logistics costs.",
  },
];
